import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './header.css';
import { Link, useLocation } from "react-router-dom";
import logo from '../../asserts/logo.png';
import { supabase } from '../../supabaseClient';

gsap.registerPlugin(ScrollTrigger);

const routeMap = {
  HOME: '/',
  EXHIBITORS: '/exhibtor',
  BOOK: '/book',
  ABOUT: '/about',
  CONTACT: '*',
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  const headerRef = useRef();
  const logoRef = useRef();
  const navRef = useRef();
  const burgerRef = useRef();
  const location = useLocation();

  const toggleMenu = () => setMenuOpen(prev => !prev);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const getNavItems = async () => {
      const res = await supabase.from('nav_bar').select('*');
      setNavItems(res.data || []);
      setDataReady(true);
    };
    getNavItems();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {

      if (headerRef.current) {
        gsap.fromTo(headerRef.current,
          { y: -90, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.9, ease: 'expo.out', delay: 4.2 }
        );
      }

      if (logoRef.current) {
        gsap.fromTo(logoRef.current,
          { scale: 0, opacity: 0, rotation: -30 },
          { scale: 1, opacity: 1, rotation: 0, duration: 0.8, ease: 'elastic.out(1, 0.5)', delay: 4.5 }
        );
      }

      gsap.fromTo('.header__nav-link',
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power3.out', delay: 4.6 }
      );

      let lastY = 0;
      let hidden = false;

      const onScroll = () => {
        const currentY = window.scrollY;
        if (currentY > 80) {
          if (currentY > lastY && !hidden) {
            gsap.to(headerRef.current, { y: -90, duration: 0.4, ease: 'power3.in' });
            hidden = true;
          } else if (currentY < lastY && hidden) {
            gsap.to(headerRef.current, { y: 0, duration: 0.5, ease: 'expo.out' });
            hidden = false;
          }
        } else {
          if (hidden) {
            gsap.to(headerRef.current, { y: 0, duration: 0.4, ease: 'expo.out' });
            hidden = false;
          }
        }
        lastY = currentY;
      };

      window.addEventListener('scroll', onScroll, { passive: true });

      document.querySelectorAll('.header__nav-link').forEach(link => {
        link.addEventListener('mouseenter', () => {
          gsap.to(link, { color: '#FF1E1E', duration: 0.25 });
        });
        link.addEventListener('mouseleave', () => {
          if (!link.classList.contains('header__nav-link--active')) {
            gsap.to(link, { color: '#FAF3E1', duration: 0.25 });
          }
        });
      });

      return () => window.removeEventListener('scroll', onScroll);
    });

    return () => ctx.revert();
  }, [dataReady]);

  useEffect(() => {
    if (!dataReady) return;
    gsap.fromTo('.header__nav-link',
      { opacity: 0.4 },
      { opacity: 1, duration: 0.4, stagger: 0.05, ease: 'power2.out' }
    );
  }, [location.pathname, dataReady]);

  return (
    <header className="header gsap-hidden" ref={headerRef}>
      <div className="header__logo" ref={logoRef}>
        <img src={logo} alt="The Design Show" />
      </div>

      <nav className={`header__nav${menuOpen ? ' header__nav--open' : ''}`} ref={navRef} aria-label="Main navigation">
        {navItems.map((item) => {
          const path = routeMap[item.nav_item_en] || '/';
          return (
            <Link
              key={item.id}
              to={path}
              onClick={closeMenu}
              className={`header__nav-link${location.pathname === path ? ' header__nav-link--active' : ''}`}
            >
              <p>{item.nav_item_en}</p>
            </Link>
          );
        })}
      </nav>

      <button
        className={`header__burger${menuOpen ? ' header__burger--open' : ''}`}
        onClick={toggleMenu}
        aria-label="Toggle menu"
        ref={burgerRef}
      >
        <svg width="28" height="20" viewBox="0 0 28 20" fill="none">
          <line className="burger-line burger-line--top" x1="0" y1="1" x2="28" y2="1" stroke="#FAF3E1" strokeWidth="2" strokeLinecap="round" />
          <line className="burger-line burger-line--mid" x1="0" y1="10" x2="28" y2="10" stroke="#FAF3E1" strokeWidth="2" strokeLinecap="round" />
          <line className="burger-line burger-line--bot" x1="0" y1="19" x2="28" y2="19" stroke="#FAF3E1" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </header>
  );
};

export default Header;